import { FC, useEffect, useState } from 'react'
import { StatusModal } from 'src/components/StatusModal'
import { useGetCompanyProfileQuery } from 'src/reactQuery/useGetCompanyProfileQuery'
import { useSaveCompanyProfileMutation } from 'src/reactQuery/useSaveCompanyProfileMutation'
import { ReactComponent as ContextIcon } from 'src/assets/svg/companyProfile/bi_file-text.svg'
import { ReactComponent as BriefcaseIcon } from 'src/assets/svg/companyProfile/boxicons_briefcase.svg'
import { ReactComponent as BuildingIcon } from 'src/assets/svg/companyProfile/carbon_building.svg'
import { ReactComponent as SwordsIcon } from 'src/assets/svg/companyProfile/charm_swords.svg'
import { ReactComponent as UsersIcon } from 'src/assets/svg/companyProfile/clarity_users-line.svg'
import { ReactComponent as PackageIcon } from 'src/assets/svg/companyProfile/codicon_package.svg'
import { ReactComponent as TargetIcon } from 'src/assets/svg/companyProfile/codicon_target.svg'
import { ChipInput } from './ChipInput'
import { CompanyProfileSkeleton } from './CompanyProfileSkeleton'
import { PriorityGrid } from './PriorityGrid'
import { SectionCard } from './SectionCard'
import styles from './companyProfile.module.scss'
import {
  CompanyProfileData,
  EMPTY_PROFILE,
  EditableSection,
  MAX_CONTEXT_LENGTH,
  MAX_ENTRY_LENGTH,
  isProfileComplete,
  isProfileEmpty,
  isSectionComplete,
  sectionPayload,
} from './types'

// Copy differs between the form and the saved summary — the form asks for
// something, the summary describes what is there.
const COPY = {
  company: {
    formSubtitle: 'Basic details that define your brand identity',
    savedSubtitle: 'Basic details defining your brand identity',
  },
  competitors: {
    formSubtitle: 'Add competitors you want to prioritize',
    savedSubtitle: 'Competitors you want to prioritize',
  },
  context: {
    formSubtitle:
      'Share anything else that could help us better understand your brand, market, or current priorities',
    savedSubtitle:
      'Nuances and background details added to direct insight generation',
  },
  priorities: {
    formSubtitle: 'Select the areas that matter most to your current strategy',
    savedSubtitle: 'The areas that matter most to your current strategy',
  },
  products: {
    formSubtitle: 'Define your offerings and the people you serve',
    savedSubtitle:
      'Your direct product offerings paired with demographic focus groups',
  },
}

export const CompanyProfile: FC = () => {
  // `saved` is what the server holds; `draft` is what the open form holds.
  // Cancel throws the draft away and puts `saved` back.
  const [saved, setSaved] = useState<CompanyProfileData | null>(null)
  const [draft, setDraft] = useState<CompanyProfileData>(EMPTY_PROFILE)
  const [editing, setEditing] = useState<EditableSection>(null)
  const [modal, setModal] = useState<'error' | 'added' | 'updated' | null>(null)
  // Which save failed, so Try Again can repeat exactly that one.
  const [failed, setFailed] = useState<'create' | 'edit' | null>(null)

  const { data, isLoading } = useGetCompanyProfileQuery()

  // Seed from the server once it answers. The form is only reset while nothing
  // is being edited, so a reply landing mid-edit cannot wipe what is typed.
  useEffect(() => {
    const profile = data?.data
    if (!profile) return
    const next: CompanyProfileData = {
      additionalContext: profile.additionalContext ?? '',
      companyName: profile.companyName ?? '',
      competitors: profile.competitors ?? [],
      otherPriority: profile.otherPriority ?? '',
      priorities: profile.priorities ?? [],
      products: profile.products ?? [],
      targetAudience: profile.targetAudience ?? [],
    }
    setSaved(next)
    setEditing((current) => {
      if (current === null) setDraft(next)
      return current
    })
  }, [data])

  const { isSaving, replace, update } = useSaveCompanyProfileMutation({
    onError: () => setModal('error'),
  })

  const isCreating = saved === null || isProfileEmpty(saved)
  const set = <K extends keyof CompanyProfileData>(
    key: K,
    value: CompanyProfileData[K]
  ) => setDraft((prev) => ({ ...prev, [key]: value }))

  const priorityCount =
    draft.priorities.length + (draft.otherPriority.trim() ? 1 : 0)

  // Applied against the previous state rather than a value computed in the
  // child, so two toggles inside one React batch both land.
  const togglePriority = (name: string) =>
    setDraft((prev) => ({
      ...prev,
      priorities: prev.priorities.includes(name)
        ? prev.priorities.filter((p) => p !== name)
        : [...prev.priorities, name],
    }))

  type ListField = 'competitors' | 'products' | 'targetAudience'

  const addTo = (field: ListField) => (name: string) =>
    setDraft((prev) =>
      prev[field].some((c) => c.toLowerCase() === name.toLowerCase())
        ? prev
        : { ...prev, [field]: [...prev[field], name] }
    )

  const removeFrom = (field: ListField) => (name: string) =>
    setDraft((prev) => ({
      ...prev,
      [field]: prev[field].filter((c) => c !== name),
    }))

  const handleCreate = () => {
    setFailed('create')
    replace(draft, {
      onSuccess: () => {
        setSaved(draft)
        setFailed(null)
        setModal('added')
      },
    })
  }

  const startEdit = (section: EditableSection) => {
    if (saved) setDraft(saved)
    setEditing(section)
  }

  const cancelEdit = () => {
    if (saved) setDraft(saved)
    setEditing(null)
  }

  const commitEdit = () => {
    setFailed('edit')
    // Only the open section is sent, which is what PATCH is for.
    update(sectionPayload(editing, draft), {
      onSuccess: () => {
        setSaved(draft)
        setEditing(null)
        setFailed(null)
        setModal('updated')
      },
    })
  }

  const retry = () => {
    setModal(null)
    if (failed === 'create') handleCreate()
    else if (failed === 'edit') commitEdit()
  }

  // ── Field blocks, shared by the create form and the per-section edit ──────
  const companyFields = (
    <div className={styles.field}>
      <div className={styles.fieldLabelRow}>
        <span className={styles.fieldLabel}>
          <BuildingIcon />
          Full Company Name
        </span>
      </div>
      <input
        className={styles.input}
        maxLength={MAX_ENTRY_LENGTH}
        onChange={(e) => set('companyName', e.target.value)}
        placeholder="Enter a company name"
        value={draft.companyName}
      />
    </div>
  )

  const competitorFields = (
    <div className={styles.field}>
      <div className={styles.fieldLabelRow}>
        <span className={styles.fieldLabel}>
          <SwordsIcon />
          Competitors
        </span>
        <span className={styles.required}>Required</span>
      </div>
      <ChipInput
        emptyPlaceholder="Example: Hapttic"
        onAdd={addTo('competitors')}
        onRemove={removeFrom('competitors')}
        value={draft.competitors}
      />
    </div>
  )

  const priorityFields = (
    <PriorityGrid
      onOtherChange={(v) => set('otherPriority', v)}
      onToggle={togglePriority}
      otherValue={draft.otherPriority}
      selected={draft.priorities}
    />
  )

  const productFields = (
    <div className={styles.twoColumn}>
      <div className={styles.field}>
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>
            <PackageIcon />
            Products / Services
          </span>
          <span className={styles.required}>Required</span>
        </div>
        <ChipInput
          emptyPlaceholder="Example: Mobile Banking, Business Loans, Credit Cards"
          onAdd={addTo('products')}
          onRemove={removeFrom('products')}
          value={draft.products}
        />
      </div>
      <div className={styles.field}>
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>
            <UsersIcon />
            Target Audience / Key Customers
          </span>
          <span className={styles.required}>Required</span>
        </div>
        <ChipInput
          emptyPlaceholder="Example: Tech-savvy millennials, Small business owners"
          onAdd={addTo('targetAudience')}
          onRemove={removeFrom('targetAudience')}
          value={draft.targetAudience}
        />
      </div>
    </div>
  )

  const contextFields = (
    <div className={styles.field}>
      <div className={styles.fieldLabelRow}>
        <span className={styles.fieldLabel}>
          <ContextIcon />
          Additional Context
        </span>
        <span className={styles.required}>Required</span>
      </div>
      <textarea
        className={styles.textarea}
        maxLength={MAX_CONTEXT_LENGTH}
        onChange={(e) => set('additionalContext', e.target.value)}
        placeholder="Enter additional information"
        value={draft.additionalContext}
      />
    </div>
  )

  const inlineActions = (
    <div className={styles.inlineActions}>
      <button
        className={styles.cancelButton}
        onClick={cancelEdit}
        type="button"
      >
        Cancel
      </button>
      <button
        className={styles.saveChangesButton}
        disabled={isSaving || !isSectionComplete(editing, draft)}
        onClick={commitEdit}
        type="button"
      >
        Save Changes
      </button>
    </div>
  )

  // ── The create form: one Save for the whole page ─────────────────────────
  const createForm = (
    <>
      <div className={styles.sections}>
        <SectionCard
          subtitle={COPY.company.formSubtitle}
          title="Company Information"
        >
          {companyFields}
        </SectionCard>

        <SectionCard
          subtitle={COPY.competitors.formSubtitle}
          title="Competitive Landscape"
        >
          {competitorFields}
        </SectionCard>

        <SectionCard
          className={styles.sectionPriorities}
          count={priorityCount > 0 ? `${priorityCount} selected` : undefined}
          required
          subtitle={COPY.priorities.formSubtitle}
          title="Business Priorities"
        >
          {priorityFields}
        </SectionCard>

        <SectionCard
          subtitle={COPY.products.formSubtitle}
          title="Products &amp; Target Audience"
        >
          {productFields}
        </SectionCard>

        <SectionCard
          subtitle={COPY.context.formSubtitle}
          title="Additional Context"
        >
          {contextFields}
        </SectionCard>
      </div>

      <div className={styles.saveRow}>
        <button
          className={styles.saveButton}
          disabled={!isProfileComplete(draft) || isSaving}
          onClick={handleCreate}
          type="button"
        >
          Save
        </button>
      </div>
    </>
  )

  // ── The saved summary, one section at a time editable ────────────────────
  const savedView = saved && (
    <div className={styles.sections}>
      {editing === 'companyInformation' ? (
        <SectionCard
          subtitle={COPY.company.formSubtitle}
          title="Company Information"
        >
          {companyFields}
          {inlineActions}
        </SectionCard>
      ) : (
        <SectionCard
          icon={<BuildingIcon />}
          onEdit={() => startEdit('companyInformation')}
          subtitle={COPY.company.savedSubtitle}
          title="Company Information"
        >
          <div>
            <p className={styles.summaryLabel}>Full Company Name</p>
            <p className={styles.summaryValue}>
              {saved.companyName.trim() || '—'}
            </p>
          </div>
        </SectionCard>
      )}

      {editing === 'competitors' ? (
        <SectionCard
          subtitle={COPY.competitors.formSubtitle}
          title="Competitive Landscape"
        >
          {competitorFields}
          {inlineActions}
        </SectionCard>
      ) : (
        <SectionCard
          icon={<SwordsIcon />}
          onEdit={() => startEdit('competitors')}
          subtitle={COPY.competitors.savedSubtitle}
          title="Competitors"
        >
          <div className={styles.summaryChips}>
            {saved.competitors.map((name) => (
              <span className={styles.summaryChip} key={name}>
                {name}
              </span>
            ))}
          </div>
        </SectionCard>
      )}

      {editing === 'priorities' ? (
        <SectionCard
          count={priorityCount > 0 ? `${priorityCount} selected` : undefined}
          required
          subtitle={COPY.priorities.formSubtitle}
          title="Business Priorities"
        >
          {priorityFields}
          {inlineActions}
        </SectionCard>
      ) : (
        <SectionCard
          icon={<TargetIcon />}
          onEdit={() => startEdit('priorities')}
          subtitle={COPY.priorities.savedSubtitle}
          title="Business Priorities"
        >
          <div className={styles.summaryChips}>
            {[
              ...saved.priorities,
              ...(saved.otherPriority.trim()
                ? [saved.otherPriority.trim()]
                : []),
            ].map((name) => (
              <span className={styles.summaryChip} key={name}>
                {name}
              </span>
            ))}
          </div>
        </SectionCard>
      )}

      {editing === 'products' ? (
        <SectionCard
          subtitle={COPY.products.formSubtitle}
          title="Products &amp; Target Audience"
        >
          {productFields}
          {inlineActions}
        </SectionCard>
      ) : (
        <SectionCard
          icon={<BriefcaseIcon />}
          onEdit={() => startEdit('products')}
          subtitle={COPY.products.savedSubtitle}
          title="Products &amp; Target Audience"
        >
          <div className={styles.twoColumn}>
            <div>
              <p className={styles.summaryLabel}>Products / Services</p>
              <div className={styles.summaryChips}>
                {saved.products.map((name) => (
                  <span className={styles.summaryChip} key={name}>
                    {name}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className={styles.summaryLabel}>
                Target Audience / Key Customers
              </p>
              <div className={styles.summaryChips}>
                {saved.targetAudience.map((name) => (
                  <span className={styles.summaryChip} key={name}>
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      {editing === 'additionalContext' ? (
        <SectionCard
          subtitle={COPY.context.formSubtitle}
          title="Additional Context"
        >
          {contextFields}
          {inlineActions}
        </SectionCard>
      ) : (
        <SectionCard
          icon={<ContextIcon />}
          onEdit={() => startEdit('additionalContext')}
          subtitle={COPY.context.savedSubtitle}
          title="Additional Context"
        >
          <p className={styles.summaryParagraph}>{saved.additionalContext}</p>
        </SectionCard>
      )}
    </div>
  )

  return (
    <div className={styles.card}>
      <div className={styles.intro}>
        <h2 className={styles.introTitle}>About Your Company</h2>
        <p className={styles.introSubtitle}>
          Tell us more about your business so we can deliver more relevant
          insights, analysis, and recommendations
        </p>
      </div>

      {isLoading ? (
        <CompanyProfileSkeleton />
      ) : isCreating ? (
        createForm
      ) : (
        savedView
      )}

      <StatusModal
        isOpen={modal === 'added'}
        message="Your company information has been successfully added and saved."
        onClose={() => setModal(null)}
        title="Company Information Added"
        type="success"
      />

      <StatusModal
        isOpen={modal === 'updated'}
        message="Your company information has been successfully updated and saved."
        onClose={() => setModal(null)}
        title="Company Information Updated"
        type="success"
      />

      <StatusModal
        doneLabel="Try Again"
        hideCountdown
        isOpen={modal === 'error'}
        message="Something went wrong while saving your information. Please try again."
        onClose={() => {
          setModal(null)
          setFailed(null)
        }}
        onSecondaryAction={retry}
        secondaryLabel="Discard"
        title="Unable to Add Company Information"
        type="error"
      />
    </div>
  )
}

export default CompanyProfile
